import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { setFormatLocale, usd as fmtUsd } from "../lib/format";
import i18n from "../i18n";

const MoneyContext = createContext(null);

const SYMBOLS = { USD: "$", EUR: "€", GBP: "£", CHF: "CHF ", JPY: "¥", CAD: "C$", AUD: "A$", INR: "₹", SEK: "kr ", NOK: "kr " };

const localeForLang = (lang) => (lang === "de" ? "de-DE" : "en-US");

export function MoneyProvider({ children }) {
  const { user, patchUser } = useAuth();
  const [homeCurrency, setHome] = useState(user?.home_currency || "USD");
  const [rate, setRate] = useState(1); // USD -> home
  const [rateReady, setRateReady] = useState((user?.home_currency || "USD") === "USD");
  const [language, setLang] = useState(
    () => user?.language || localStorage.getItem("costock_lang") || "en"
  );
  const [numberLocale, setNumLocale] = useState(
    () => user?.number_locale || localStorage.getItem("costock_numlocale") || localeForLang(user?.language || localStorage.getItem("costock_lang") || "en")
  );
  const [marginalTaxRate, setMarginalTaxRateState] = useState(user?.marginal_tax_rate ?? 42);
  const [churchTaxPct, setChurchTaxPctState] = useState(user?.church_tax_pct ?? 0);
  const [married, setMarriedState] = useState(user?.married ?? false);

  // Apply locale to formatters + i18n on first render / when derived values load.
  useEffect(() => {
    setFormatLocale(numberLocale);
    localStorage.setItem("costock_numlocale", numberLocale);
  }, [numberLocale]);

  useEffect(() => {
    localStorage.setItem("costock_lang", language);
    if (i18n.language !== language) i18n.changeLanguage(language);
  }, [language]);

  // When the authenticated user profile loads, hydrate preferences from it.
  useEffect(() => {
    if (user?.home_currency) setHome(user.home_currency);
    if (user?.language) setLang(user.language);
    if (user?.number_locale) setNumLocale(user.number_locale);
    if (user?.marginal_tax_rate != null) setMarginalTaxRateState(user.marginal_tax_rate);
    if (user?.church_tax_pct != null) setChurchTaxPctState(user.church_tax_pct);
    if (user?.married != null) setMarriedState(user.married);
  }, [user?.home_currency, user?.language, user?.number_locale, user?.marginal_tax_rate, user?.church_tax_pct, user?.married]);

  useEffect(() => {
    if (!homeCurrency || homeCurrency === "USD") { setRate(1); setRateReady(true); return; }
    let cancelled = false;
    let attempt = 0;
    setRateReady(false);
    const tryFetch = () => {
      api.fx("USD", homeCurrency).then((r) => {
        if (cancelled) return;
        setRate(r.data.rate);
        setRateReady(true);
      }).catch(() => {
        // Don't fall back to a fake 1:1 rate on failure — that silently shows the
        // wrong number (home currency displayed as if equal to USD). Retry a few
        // times instead, and only give up (still not "ready") after that.
        if (cancelled) return;
        attempt += 1;
        if (attempt < 5) setTimeout(tryFetch, 3000);
      });
    };
    tryFetch();
    return () => { cancelled = true; };
  }, [homeCurrency]);

  const setHomeCurrency = useCallback(async (ccy) => {
    const prev = homeCurrency;
    setHome(ccy);
    try {
      const { data } = await api.updateSettings({ home_currency: ccy });
      patchUser({ home_currency: data.home_currency });
    } catch {
      setHome(prev);
      toast.error("Could not save currency preference");
    }
  }, [homeCurrency, patchUser]);

  const setLanguage = useCallback(async (lang) => {
    setLang(lang);
    i18n.changeLanguage(lang);
    // If the user never explicitly overrode number format, follow the language.
    if (!user?.number_locale && !localStorage.getItem("costock_numlocale_manual")) {
      setNumLocale(localeForLang(lang));
    }
    try {
      const { data } = await api.updateSettings({ language: lang });
      patchUser({ language: data.language });
    } catch { /* non-blocking */ }
  }, [patchUser, user?.number_locale]);

  const setNumberLocale = useCallback(async (loc) => {
    setNumLocale(loc);
    setFormatLocale(loc);
    localStorage.setItem("costock_numlocale", loc);
    localStorage.setItem("costock_numlocale_manual", "1");
    // Force all i18n consumers to re-render so new number formatting shows immediately.
    i18n.changeLanguage(i18n.language);
    try {
      const { data } = await api.updateSettings({ number_locale: loc });
      patchUser({ number_locale: data.number_locale });
    } catch { /* non-blocking */ }
  }, [patchUser]);

  const setMarginalTaxRate = useCallback(async (rate) => {
    const prev = marginalTaxRate;
    setMarginalTaxRateState(rate);
    try {
      const { data } = await api.updateSettings({ marginal_tax_rate: rate });
      patchUser({ marginal_tax_rate: data.marginal_tax_rate });
    } catch {
      setMarginalTaxRateState(prev);
      toast.error("Could not save tax rate");
    }
  }, [marginalTaxRate, patchUser]);

  const setChurchTaxPct = useCallback(async (pct) => {
    const prev = churchTaxPct;
    setChurchTaxPctState(pct);
    try {
      const { data } = await api.updateSettings({ church_tax_pct: pct });
      patchUser({ church_tax_pct: data.church_tax_pct });
    } catch {
      setChurchTaxPctState(prev);
      toast.error("Could not save church tax setting");
    }
  }, [churchTaxPct, patchUser]);

  const setMarried = useCallback(async (val) => {
    const prev = married;
    setMarriedState(val);
    try {
      const { data } = await api.updateSettings({ married: val });
      patchUser({ married: data.married });
    } catch {
      setMarriedState(prev);
      toast.error("Could not save setting");
    }
  }, [married, patchUser]);

  const hasHome = !!homeCurrency && homeCurrency !== "USD" && rateReady;

  const homeStr = (vUsd) => {
    if (!hasHome || vUsd == null || isNaN(vUsd)) return null;
    const sym = SYMBOLS[homeCurrency] || "";
    const val = new Intl.NumberFormat(numberLocale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(vUsd * rate);
    return `${sym}${val}`;
  };

  // When a home currency is set, it takes over as the main figure (primary) and USD
  // becomes the small "≈" line — otherwise USD stays primary with nothing secondary.
  const display = (vUsd, opts) => {
    const usdStr = fmtUsd(vUsd, opts);
    if (hasHome) return { primary: homeStr(vUsd), secondary: usdStr };
    return { primary: usdStr, secondary: null };
  };

  return (
    <MoneyContext.Provider value={{
      homeCurrency, setHomeCurrency, rate, hasHome, homeStr, display, language, setLanguage, numberLocale, setNumberLocale,
      marginalTaxRate, setMarginalTaxRate, churchTaxPct, setChurchTaxPct, married, setMarried,
    }}>
      {children}
    </MoneyContext.Provider>
  );
}

export const useMoney = () => useContext(MoneyContext);
